<template>
  <div class="warehouse-layout-management">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <div class="title-block">
            <h2>仓库视图 - {{ selectedWarehouse?.location_name || '选择仓库' }}</h2>
            <p class="title-hint">
              {{
                editMode
                  ? '编辑模式：在网格中调整库位布局。'
                  : '浏览库位与库存；上方可开启盘点轮次，在库存对话框中盘点、盖章。本轮结束后印记清零，历史可查，支持多次盘存。'
              }}
            </p>
          </div>
          <div class="header-actions">
            <el-select
              v-model="selectedWarehouseId"
              placeholder="选择仓库"
              @change="handleWarehouseChange"
              style="width: 200px; margin-right: 10px;"
            >
              <el-option
                v-for="warehouse in warehouseList"
                :key="warehouse.location_id"
                :label="warehouse.location_name"
                :value="warehouse.location_id"
              />
            </el-select>
            
            <el-button
              v-if="canEditLocations"
              @click="toggleEditMode"
              :type="editMode ? 'danger' : 'primary'"
            >
              <el-icon><Edit /></el-icon>
              {{ editMode ? '退出编辑' : '编辑布局' }}
            </el-button>
            
            <el-button @click="resetView">
              <el-icon><Refresh /></el-icon>
              重置视图
            </el-button>
            
            <el-button
              v-if="canEditLocations && editMode"
              @click="saveGridSettings"
              type="success"
            >
              <el-icon><Check /></el-icon>
              保存网格设置
            </el-button>
            
            <el-button @click="checkLocationData" type="warning">
              <el-icon><InfoFilled /></el-icon>
              检查数据
            </el-button>
          </div>
        </div>
      </template>

      <StockTakeRoundBar
        v-if="selectedWarehouseId && !editMode"
        :open-round="openRound"
        :open-rounds="openRounds"
        :has-open-round="hasOpenRound"
        :can-edit="canManageRound"
        :can-admin-void-stamps="isStockTakeAdmin"
        :action-loading="roundActionLoading"
        :progress-text="stockTakeProgressText"
        :scope-summary-fn="scopeSummary"
        @start="openStartRoundDialog"
        @end="onEndRound"
        @history="openStockTakeHistory"
        @void-before="openVoidBeforeDialog"
      />

      <el-dialog v-model="voidBeforeDialogVisible" title="批量作废盘点印记" width="480px" destroy-on-close>
        <el-alert
          type="warning"
          :closable="false"
          show-icon
          title="仅作废库位「已盘查」盖章印记，不会清除材料上的「已盘」勾选。"
          style="margin-bottom: 12px"
        />
        <el-form label-width="120px">
          <el-form-item label="印记时间早于" required>
            <el-date-picker
              v-model="voidBeforeForm.before"
              type="datetime"
              placeholder="选择截止时间"
              style="width: 100%"
              value-format="YYYY-MM-DD HH:mm:ss"
              teleported
              popper-class="m-stock-take-datepicker-popper"
            />
          </el-form-item>
          <el-form-item label="范围">
            <el-radio-group v-model="voidBeforeForm.scope">
              <el-radio value="warehouse">仅当前仓库</el-radio>
              <el-radio value="all">全部仓库</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
        <p class="bin-dialog-sub">此操作仅管理员可用，提交前会再次确认。</p>
        <template #footer>
          <el-button @click="voidBeforeDialogVisible = false">取消</el-button>
          <el-button type="danger" :loading="voidBeforeLoading" @click="confirmVoidBefore">作废印记</el-button>
        </template>
      </el-dialog>

      <el-dialog v-model="startRoundDialogVisible" title="开启盘点轮次" width="520px" destroy-on-close>
        <el-form label-width="96px">
          <el-form-item label="轮次名称">
            <el-input v-model="startRoundForm.name" placeholder="可选，默认自动生成" maxlength="120" />
          </el-form-item>
          <el-form-item label="范围">
            <el-radio-group v-model="startRoundForm.scope_mode">
              <el-radio value="warehouse">整仓</el-radio>
              <el-radio value="partial">指定区域/库位</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="startRoundForm.scope_mode === 'partial'" label="选择范围">
            <el-tree
              ref="startRoundTreeRef"
              :data="startRoundTreeData"
              show-checkbox
              node-key="id"
              :props="{ label: 'label', children: 'children' }"
              default-expand-all
              style="width: 100%; max-height: 320px; overflow: auto; border: 1px solid #ebeef5; padding: 8px"
            />
            <p class="bin-dialog-sub" style="margin-top: 8px">勾选区域会包含其全部下级库位；可同时选多个区域做一次盘点。</p>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="startRoundDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="roundActionLoading" @click="confirmStartRound">开启</el-button>
        </template>
      </el-dialog>

      <!-- 编辑模式控制面板 -->
      <div class="layout-controls" v-if="editMode">
        <el-alert
          :title="editModeTip"
          type="info"
          :closable="false"
          style="margin-bottom: 15px;"
        />
        
        <div class="control-panel">
          <span>格子大小：</span>
          <el-slider
            v-model="gridSize"
            :min="40"
            :max="120"
            :step="10"
            @change="updateContainerSize"
            style="width: 200px; display: inline-block; margin: 0 15px;"
          />
          <span>{{ gridSize }}px</span>
          
          <span>网格列数：</span>
          <el-input-number
            v-model="gridColumns"
            :min="10"
            :max="50"
            :step="5"
            @change="updateContainerSize"
            style="width: 100px; margin: 0 15px;"
          />
          
          <span>网格行数：</span>
          <el-input-number
            v-model="gridRows"
            :min="10"
            :max="50"
            :step="5"
            @change="updateContainerSize"
            style="width: 100px; margin: 0 15px;"
          />
          
          <span>显示网格：</span>
          <el-switch v-model="showGrid" style="margin: 0 15px;" />
          
          <el-button size="small" @click="autoArrange">自动排列</el-button>
        </div>

        <!-- 选中的库位操作 -->
        <div class="selected-location-controls" v-if="selectedLocation">
          <el-divider content-position="left">选中库位操作</el-divider>
          <div class="location-actions">
            <span>当前选中: <strong>{{ selectedLocation.location_name }}</strong></span>
            
            <div class="size-controls">
              <span>尺寸调整:</span>
              <el-input-number
                v-model="selectedLocation.layout_width"
                :min="1"
                :max="50"
                size="small"
                @change="handleSizeChange"
                style="width: 80px; margin: 0 10px;"
              />
              <span>×</span>
              <el-input-number
                v-model="selectedLocation.layout_height"
                :min="1"
                :max="50"
                size="small"
                @change="handleSizeChange"
                style="width: 80px; margin: 0 10px;"
              />
            </div>
            
            <div class="move-controls">
              <span>移动操作:</span>
              <el-button-group>
                <el-button 
                  size="small" 
                  @mousedown="startContinuousMove('up')" 
                  @mouseup="stopContinuousMove"
                  @mouseleave="stopContinuousMove"
                  title="上移"
                >
                  <el-icon><ArrowUp /></el-icon>
                </el-button>
                <el-button 
                  size="small" 
                  @mousedown="startContinuousMove('down')" 
                  @mouseup="stopContinuousMove"
                  @mouseleave="stopContinuousMove"
                  title="下移"
                >
                  <el-icon><ArrowDown /></el-icon>
                </el-button>
                <el-button 
                  size="small" 
                  @mousedown="startContinuousMove('left')" 
                  @mouseup="stopContinuousMove"
                  @mouseleave="stopContinuousMove"
                  title="左移"
                >
                  <el-icon><ArrowLeft /></el-icon>
                </el-button>
                <el-button 
                  size="small" 
                  @mousedown="startContinuousMove('right')" 
                  @mouseup="stopContinuousMove"
                  @mouseleave="stopContinuousMove"
                  title="右移"
                >
                  <el-icon><ArrowRight /></el-icon>
                </el-button>
              </el-button-group>
              <span class="move-tip">(按住连续移动)</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 文件夹浏览（默认），交互对齐库位管理 -->
      <div v-if="!editMode" v-loading="loading" class="folder-view wl-folder-view">
        <div class="breadcrumb-nav">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item v-for="(c, i) in browsePath" :key="`${c.id}-${i}`">
              <el-button text type="primary" @click="goBrowseCrumb(i)">{{ c.name }}</el-button>
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <div v-if="folderChildrenWithMeta.length === 0" class="empty-state">
          <el-empty description="当前目录下暂无库位" />
        </div>
        <div v-else class="folder-list">
          <div
            v-for="item in folderChildrenWithMeta"
            :key="item.location_id"
            class="folder-item-row"
            :class="{ 'has-children': item.hasChildren, clickable: true }"
            @click="onBrowseRowClick(item)"
          >
            <div class="folder-item-content">
              <el-icon class="type-icon" :style="{ color: getIconColor(item.location_type) }">
                <component :is="getIconComponent(item.location_type)" />
              </el-icon>
              <span class="folder-name">{{ item.location_name }}</span>
              <el-tag :type="getLocationTypeTagType(item.location_type)" size="small" class="type-tag">
                {{ getLocationTypeText(item.location_type) }}
              </el-tag>
              <span class="folder-code">{{ item.location_code || '—' }}</span>
              <span v-if="item.hasChildren" class="folder-count">（含下级结构）</span>
              <div
                v-if="item.inventory_summary && Number(item.inventory_summary.total_quantity) > 0"
                class="folder-inv-hint"
              >
                本节点登记：{{ item.inventory_summary.total_items }} 种 ·
                {{ formatQty(item.inventory_summary.total_quantity) }} 件 ·
                {{ formatBranchRolls(item.inventory_summary) }} 支
              </div>
              <span
                v-else-if="showEmptyCharForBrowseItem(item)"
                class="wl-storage-empty-char"
                aria-label="本库位无在库实物"
              >空</span>
              <div
                v-else-if="item.hasChildren"
                class="folder-inv-hint muted"
              >本节点无直接库存记录（可能在下级）</div>
              <div
                v-if="item.last_stock_take?.operator_name"
                class="folder-stock-take"
              >
                {{ item.last_stock_take.operator_name }} · {{ formatStockTakeTime(item.last_stock_take.checked_at) }} 已盘查
              </div>
              <div class="folder-actions" @click.stop>
                <el-button v-if="item.hasChildren" size="small" type="primary" @click="enterBrowseFolder(item)">
                  进入
                </el-button>
                <el-button
                  v-if="item.hasChildren"
                  size="small"
                  @click="openBinInventoryDetail(item)"
                >
                  下级汇总库存
                </el-button>
                <el-button v-else size="small" type="success" @click="openBinInventoryDetail(item)">
                  查看库存
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 仓库平面视图（仅编辑模式） -->
      <div v-else class="warehouse-layout-container">
        <div 
          class="warehouse-map-canvas"
          :style="containerStyle"
          @keydown="handleKeydown"
          @keyup="handleKeyup"
          @contextmenu.prevent="handleRightClick"
          tabindex="0"
          ref="containerElement"
        >
          <!-- 网格背景 -->
          <div 
            v-for="cell in gridCells" 
            :key="`cell-${cell.x}-${cell.y}`"
            class="grid-cell"
            :style="getCellStyle(cell)"
          >
            <span class="grid-coord">{{ cell.x }},{{ cell.y }}</span>
          </div>
          
          <!-- 库位方块 -->
          <div
            v-for="location in visibleLocations"
            :key="location.location_id"
            class="location-block"
            :style="getLocationStyle(location)"
            :class="{ 
              'selected': selectedLocationId === location.location_id,
              'dispatch-complete-transit-block': location.is_dispatch_complete_transit
            }"
            @click="handleLocationClick(location)"
            @dblclick="handleLocationDoubleClick(location)"
            @contextmenu.prevent="handleLocationRightClick(location)"
          >
            <div class="location-content">
              <div class="location-header">
                <el-tag size="small" effect="dark" class="location-type-tag">
                  {{ getLocationTypeText(location.location_type) }}
                </el-tag>
                <span class="location-name">{{ location.location_name }}</span>
              </div>
              
              <div class="location-position">
                ({{ location.layout_x }}, {{ location.layout_y }}) 
                {{ location.layout_width }}×{{ location.layout_height }}
              </div>
              
              <div class="inventory-info" v-if="location.inventory_summary && Number(location.inventory_summary.total_quantity) > 0">
                <div class="inventory-stats">
                  <span class="stat-item">
                    <el-icon><Box /></el-icon>
                    {{ location.inventory_summary.total_items }} 种
                  </span>
                  <span class="stat-item">
                    <el-icon><Collection /></el-icon>
                    {{ location.inventory_summary.total_quantity }} 件
                  </span>
                  <span class="stat-item">
                    <el-icon><Tickets /></el-icon>
                    {{ formatBranchRolls(location.inventory_summary) }} 支
                  </span>
                </div>
              </div>
              <div
                v-else-if="showEmptyCharForLayoutBlock(location)"
                class="location-block-empty-char"
                aria-label="本库位无在库实物"
              >空</div>
              <div
                v-if="location.last_stock_take?.operator_name"
                class="location-stock-take"
              >
                {{ location.last_stock_take.operator_name }} · {{ formatStockTakeTime(location.last_stock_take.checked_at) }} 已盘查
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 库位编辑对话框 -->
    <!-- 本级及下级库位材料库存汇总 -->
    <el-dialog
      v-model="binInventoryDialogVisible"
      :title="`库存汇总 — ${binDialogLocation?.location_name || ''}`"
      width="1080px"
      destroy-on-close
      @closed="onBinDialogClosed"
    >
      <div v-if="binDialogLocation" class="bin-dialog-sub">
        {{ binDialogLocation.location_code }} · {{ getLocationTypeText(binDialogLocation.location_type) }}
        <span v-if="inventorySubtreeLocationIds.length">
          · 汇总范围 {{ inventorySubtreeLocationIds.length }} 个位置（含本级及全部下级）
        </span>
      </div>
      <div v-if="binDialogLocation?.last_stock_take?.operator_name" class="bin-dialog-take">
        {{ binDialogLocation.last_stock_take.operator_name }} ·
        {{ formatStockTakeTime(binDialogLocation.last_stock_take.checked_at) }} 已盘查（本轮）
        <el-button
          v-if="canStockTakeEdit && binDialogLocation.last_stock_take.session_id"
          link
          type="danger"
          size="small"
          @click="voidBinStamp"
        >
          作废印记
        </el-button>
      </div>

      <div class="bin-st-toolbar">
        <span>盘点模式</span>
        <el-switch v-model="desktopStockTakeMode" :disabled="!canStockTakeEdit" />
        <template v-if="desktopStockTakeMode">
          <el-alert
            v-if="!binInOpenRound"
            type="warning"
            :closable="false"
            show-icon
            title="当前库位不在进行中的盘点范围内，请管理员按区域开启盘点后再盖章/勾选"
            style="margin: 0; flex: 1"
          />
          <template v-else>
            <el-button
              type="primary"
              size="small"
              :loading="stampSaving"
              :disabled="!canStockTakeEdit || !canStampCurrentBin"
              @click="stampCurrentBin"
            >
              确认本库位已盘查
              <template v-if="binInventoryRows.length">
                （{{ stampedBinInvCount }}/{{ binInventoryRows.length }}）
              </template>
            </el-button>
            <el-button
              v-if="canStockTakeEdit"
              type="warning"
              plain
              size="small"
              @click="openDesktopIntake"
            >
              未知料号·申请入库
            </el-button>
            <el-alert
              v-if="uncheckedBinInvRows.length"
              type="warning"
              :closable="false"
              show-icon
              style="margin: 0; flex: 1; min-width: 200px"
              :title="`尚有 ${uncheckedBinInvRows.length} 条未勾「已盘」，全部勾选后方可盖章；实物不在本库位请先移库或报损`"
            />
          </template>
        </template>
      </div>

      <el-table
        :data="binInventoryRows"
        v-loading="binInventoryLoading"
        max-height="420"
        border
        stripe
        style="width: 100%"
        empty-text="该范围内暂无库存记录"
        :row-class-name="binInventoryRowClassName"
      >
        <el-table-column label="存放库位" min-width="110">
          <template #default="{ row }">
            {{ row.storage_location?.location_name || row.storage_location?.location_code || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="材料身份" min-width="110">
          <template #default="{ row }">
            {{ row.material_identity?.material_identity_code || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="材料名称" min-width="100">
          <template #default="{ row }">
            {{ row.material_identity?.material_category?.internal_name || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="规格" min-width="120">
          <template #default="{ row }">
            {{ row.material_identity?.specification || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="颜色" min-width="100">
          <template #default="{ row }">
            <span v-if="row.material_identity?.color">
              {{ row.material_identity.color.internal_name_cn || row.material_identity.color.internal_name_en }}
            </span>
            <span v-else>—</span>
          </template>
        </el-table-column>
        <el-table-column label="系统料号" min-width="110">
          <template #default="{ row }">
            {{ row.material_batch?.system_batch_number || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="仓库编号" width="100">
          <template #default="{ row }">
            {{ row.warehouse_manual_code || row.material_batch?.manual_batch_number || '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="physical_quantity" label="账面(码)" width="96" align="right" />
        <el-table-column v-if="desktopStockTakeMode" label="已盘" width="72" align="center">
          <template #default="{ row }">
            <el-checkbox
              :model-value="!!desktopLineState(row).checked"
              :disabled="!canStockTakeEdit || !binInOpenRound || !row.inventory_id"
              @change="(v) => onDesktopCheckChange(row, v)"
            />
          </template>
        </el-table-column>
        <el-table-column v-if="desktopStockTakeMode" label="实盘(码)" width="120">
          <template #default="{ row }">
            <el-input
              v-model="desktopLineState(row).countedStr"
              size="small"
              placeholder="实盘数量"
              :disabled="!canStockTakeEdit || !binInOpenRound || !row.inventory_id"
              @change="() => persistDesktopCounted(row)"
            />
          </template>
        </el-table-column>
        <el-table-column v-if="desktopStockTakeMode && canAdjustStock" label="调整" width="150">
          <template #default="{ row }">
            <el-button link type="success" size="small" @click="openDesktopQty(row, 'overflow')">报溢</el-button>
            <el-button link type="warning" size="small" @click="openDesktopQty(row, 'loss')">报损</el-button>
          </template>
        </el-table-column>
        <el-table-column prop="inventory_status" label="状态" width="80" />
      </el-table>
      <template #footer>
        <el-button @click="binInventoryDialogVisible = false">关闭</el-button>
        <el-button
          type="primary"
          @click="goInventoryPageForBin"
          :disabled="!binDialogLocation"
        >
          在库存管理中打开
        </el-button>
      </template>
    </el-dialog>

    <el-drawer v-model="historyDrawerVisible" title="盘点历史" size="420px" destroy-on-close>
      <div v-loading="historyLoading">
        <div v-for="r in historyRounds" :key="r.round_id" class="st-hist-round">
          <div class="st-hist-round__head">
            <strong>{{ r.name || `轮次 #${r.round_id}` }}</strong>
            <el-tag size="small" :type="r.status === 'open' ? 'success' : 'info'">
              {{ r.status === 'open' ? '进行中' : '已结束' }}
            </el-tag>
          </div>
          <div class="st-hist-round__meta">
            范围
            {{
              (r.scope_mode || 'warehouse') === 'partial'
                ? (r.scope_location_labels || []).join('、') || `指定 ${(r.scope_location_ids || []).length} 处`
                : '整仓'
            }}
            · 开始 {{ formatStockTakeTime(r.started_at) }}
            <template v-if="r.closed_at"> · 结束 {{ formatStockTakeTime(r.closed_at) }}</template>
          </div>
          <el-button link type="primary" size="small" @click="toggleHistorySessions(r)">
            {{ historySessionsMap[r.round_id] ? '收起盖章' : '查看盖章' }}
          </el-button>
          <ul v-if="historySessionsMap[r.round_id]" class="st-hist-sessions">
            <li v-for="s in historySessionsMap[r.round_id]" :key="s.session_id">
              #{{ s.session_id }} · 库位 {{ s.storage_location_id }} ·
              {{ s.operator_name || '—' }} · {{ formatStockTakeTime(s.created_at) }}
            </li>
            <li v-if="!historySessionsMap[r.round_id].length" class="muted">本轮暂无盖章记录</li>
          </ul>
        </div>
        <el-empty v-if="!historyLoading && !historyRounds.length" description="暂无盘点轮次" />
      </div>
    </el-drawer>

    <el-dialog v-model="desktopQtyVisible" :title="desktopQtyMode === 'overflow' ? '报溢入库' : '报损出库'" width="420px">
      <p v-if="desktopQtyRow">当前账面 <strong>{{ desktopQtyRow.physical_quantity }}</strong></p>
      <el-form label-width="100px">
        <el-form-item :label="desktopQtyMode === 'overflow' ? '增加数量' : '减少数量'">
          <el-input v-model="desktopQtyForm.deltaInput" placeholder="必填其一" />
        </el-form-item>
        <el-form-item :label="desktopQtyMode === 'overflow' ? '实盘数量' : '报损后数量'">
          <el-input v-model="desktopQtyForm.actualInput" placeholder="或填此项" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="desktopQtyForm.notes" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="desktopQtyVisible = false">取消</el-button>
        <el-button type="primary" :loading="desktopQtySubmitting" @click="submitDesktopQty">提交</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="desktopIntakeVisible" title="未知料号·申请入库" width="440px">
      <el-form label-width="100px">
        <el-form-item label="库位" required>
          <el-select v-model="desktopIntakeForm.storage_location_id" filterable style="width: 100%">
            <el-option
              v-for="id in inventorySubtreeLocationIds"
              :key="id"
              :label="locationLabelById(id)"
              :value="id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="仓库料号" required>
          <el-input v-model="desktopIntakeForm.warehouse_manual_code" maxlength="64" />
        </el-form-item>
        <el-form-item label="数量（码）" required>
          <el-input v-model="desktopIntakeForm.quantityStr" />
        </el-form-item>
        <el-form-item label="支数">
          <el-input v-model="desktopIntakeForm.rollCountStr" placeholder="选填" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="desktopIntakeForm.applicant_notes" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="desktopIntakeVisible = false">取消</el-button>
        <el-button type="primary" :loading="desktopIntakeSubmitting" @click="submitDesktopIntake">提交申请</el-button>
      </template>
    </el-dialog>

    <el-dialog
      :title="`编辑库位 - ${editingLocation.location_name}`"
      v-model="showLocationEditDialog"
      width="500px"
      @closed="resetEditingLocation"
    >
      <el-form :model="editingLocation" label-width="100px" ref="editFormRef">
        <el-form-item label="库位名称">
          <el-input v-model="editingLocation.location_name" disabled />
        </el-form-item>
        
        <el-form-item label="位置坐标">
          <div style="display: flex; align-items: center; gap: 10px;">
            X: <el-input-number 
              v-model="editingLocation.layout_x" 
              :min="0" 
              :max="gridColumns-1"
              controls-position="right"
            />
            Y: <el-input-number 
              v-model="editingLocation.layout_y" 
              :min="0" 
              :max="gridRows-1"
              controls-position="right"
            />
          </div>
        </el-form-item>
        
        <el-form-item label="尺寸">
          <div style="display: flex; align-items: center; gap: 10px;">
            宽: <el-input-number 
              v-model="editingLocation.layout_width" 
              :min="1" 
              :max="50"
              controls-position="right"
            />
            高: <el-input-number 
              v-model="editingLocation.layout_height" 
              :min="1" 
              :max="50"
              controls-position="right"
            />
          </div>
        </el-form-item>
        
        <el-form-item label="颜色">
          <el-color-picker 
            v-model="editingLocation.layout_color" 
            show-alpha
            :predefine="predefineColors"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showLocationEditDialog = false">取消</el-button>
          <el-button type="primary" @click="handleDialogSave">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Edit,
  Refresh,
  Box,
  Collection,
  InfoFilled,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Check,
  OfficeBuilding,
  MapLocation,
  Grid,
  Tickets
} from '@element-plus/icons-vue'
import { getWarehouseLayout, updateLocationLayout } from '@/api/warehouseLayout'
import { getStorageLocations } from '@/api/storageLocations'
import { getMaterialInventory } from '@/api/materialInventory'
import { getInventoryTransactionTypes } from '@/api/inventoryTransactionTypes'
import { createInventoryTransaction } from '@/api/inventoryTransactions'
import { getUnits } from '@/api/units'
import { createInventoryManualCodeIntakeRequest } from '@/api/inventoryManualCodeIntakeRequests'
import {
  patchMobileStockTakeLineMark,
  voidStockTakeSessionsBefore
} from '@/api/inventoryStockTake'
import { isDispatchCompleteTransitRow } from '@/utils/inventoryTransactionDisplay'
import {
  interpretInventoryTransactionResponse,
  notifyInventoryTransactionResult
} from '@/utils/inventoryTransactionResponse'
import { useUserStore } from '@/store/user'
import { hasPermission } from '@/utils/permission'
import { useStockTakeRounds } from '@/composables/useStockTakeRounds'
import StockTakeRoundBar from '@/components/inventory/StockTakeRoundBar.vue'

const router = useRouter()
const userStore = useUserStore()
const canEditLocations = computed(() =>
  hasPermission(userStore.user, 'inventory.locations.edit')
)
const canStockTakeEdit = computed(() =>
  hasPermission(userStore.user, 'inventory.records.edit') ||
  hasPermission(userStore.user, 'inventory.records.review') ||
  hasPermission(userStore.user, 'inventory.operations.edit') ||
  hasPermission(userStore.user, 'inventory.operations.review')
)
const canAdjustStock = computed(() => hasPermission(userStore.user, 'inventory.operations.edit'))
const isStockTakeAdmin = computed(
  () => !!(userStore.user?.is_admin || userStore.user?.is_superuser)
)

// 响应式数据
const loading = ref(false)
const selectedWarehouseId = ref(null)

const {
  openRound,
  openRounds,
  hasOpenRound,
  canManageRound,
  roundActionLoading,
  stampedLocationIds,
  loadOpenRound,
  startRound,
  endRound,
  stampLocation,
  voidLatestStamp,
  listRounds,
  listSessionsForRound,
  refreshRoundProgress,
  applyOpenRoundsList,
  scopeSummary
} = useStockTakeRounds(() => selectedWarehouseId.value)

const stockTakeProgressText = computed(() => {
  if (!hasOpenRound.value) return ''
  const stamped = stampedLocationIds.value.length
  return `进行中 ${openRounds.value.length} 轮 · 已盖章库位合计 ${stamped}`
})

const startRoundDialogVisible = ref(false)
const startRoundTreeRef = ref(null)
const startRoundForm = reactive({
  name: '',
  scope_mode: 'warehouse'
})

const voidBeforeDialogVisible = ref(false)
const voidBeforeLoading = ref(false)
const voidBeforeForm = reactive({
  before: '',
  scope: 'warehouse'
})

function openVoidBeforeDialog() {
  if (!isStockTakeAdmin.value) {
    ElMessage.warning('仅管理员可批量作废盘点印记')
    return
  }
  voidBeforeForm.before = ''
  voidBeforeForm.scope = 'warehouse'
  voidBeforeDialogVisible.value = true
}

async function confirmVoidBefore() {
  if (!isStockTakeAdmin.value) {
    ElMessage.warning('仅管理员可批量作废盘点印记')
    return
  }
  if (!voidBeforeForm.before) {
    ElMessage.warning('请选择印记时间截止点')
    return
  }
  const scopeLabel =
    voidBeforeForm.scope === 'warehouse'
      ? `当前仓库「${selectedWarehouse.value?.location_name || selectedWarehouseId.value}」`
      : '全部仓库'
  try {
    await ElMessageBox.confirm(
      `将作废 ${scopeLabel} 中，印记时间早于「${voidBeforeForm.before}」的全部盘查盖章。\n材料上的「已盘」勾选不会清除。\n\n确认执行？`,
      '确认批量作废印记',
      { type: 'warning', confirmButtonText: '确认作废', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  voidBeforeLoading.value = true
  try {
    const payload = { before: voidBeforeForm.before }
    if (voidBeforeForm.scope === 'warehouse') {
      if (!selectedWarehouseId.value) {
        ElMessage.warning('请先选择仓库')
        return
      }
      payload.warehouse_location_id = selectedWarehouseId.value
    }
    const res = await voidStockTakeSessionsBefore(payload)
    if (res?.success) {
      ElMessage.success(res.message || '已作废')
      voidBeforeDialogVisible.value = false
      await loadOpenRound()
      await loadWarehouseLayout()
    } else {
      ElMessage.error(res?.message || '作废失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '作废失败')
  } finally {
    voidBeforeLoading.value = false
  }
}

const startRoundTreeData = computed(() => {
  const wid = selectedWarehouseId.value
  if (!wid) return []
  const byParent = {}
  for (const loc of layoutData.value) {
    const pid = loc.parent_location_id == null ? null : Number(loc.parent_location_id)
    if (!byParent[pid]) byParent[pid] = []
    byParent[pid].push(loc)
  }
  const build = (parentId) => {
    const kids = byParent[parentId] || byParent[Number(parentId)] || []
    return kids.map(loc => ({
      id: Number(loc.location_id),
      label: `${loc.location_name || ''} (${loc.location_code || loc.location_id}) · ${getLocationTypeText(loc.location_type)}`,
      children: build(loc.location_id)
    }))
  }
  // 根：仓库本身的直接下级；也可勾选仓库整仓用 radio
  return build(wid)
})

function openStartRoundDialog() {
  if (!canManageRound.value) {
    ElMessage.warning('仅管理员可开启盘点轮次')
    return
  }
  startRoundForm.name = ''
  startRoundForm.scope_mode = 'warehouse'
  startRoundDialogVisible.value = true
}

async function confirmStartRound() {
  let scope_location_ids = []
  if (startRoundForm.scope_mode === 'partial') {
    const tree = startRoundTreeRef.value
    const checked = tree?.getCheckedKeys?.(false) || []
    const half = tree?.getHalfCheckedKeys?.() || []
    // 只用完全勾选的节点作为根，避免半选父节点重复扩范围
    scope_location_ids = [...checked].map(Number).filter(Boolean)
    if (!scope_location_ids.length && half.length) {
      ElMessage.warning('请完整勾选要盘点的区域或库位')
      return
    }
    if (!scope_location_ids.length) {
      ElMessage.warning('请选择要盘点的区域或库位')
      return
    }
  }
  const r = await startRound({
    name: startRoundForm.name || undefined,
    scope_mode: startRoundForm.scope_mode,
    scope_location_ids
  })
  if (r) {
    startRoundDialogVisible.value = false
    await loadWarehouseLayout()
  }
}

async function onEndRound(roundId) {
  const ok = await endRound(roundId)
  if (ok && selectedWarehouseId.value) {
    await loadWarehouseLayout()
  }
}

const binInOpenRound = computed(() => {
  const loc = binDialogLocation.value
  if (!loc) return false
  if (loc.open_stock_take_round_id) return true
  // 兼容：根据 openRounds + 布局树判断是否覆盖
  return !!coveringRoundIdForLocation(loc.location_id)
})

function coveringRoundIdForLocation(locationId) {
  const lid = Number(locationId)
  if (!lid || !openRounds.value.length) return null
  const ancestors = new Set([lid])
  let cur = layoutData.value.find(l => Number(l.location_id) === lid)
  const guard = new Set()
  while (cur?.parent_location_id != null && !guard.has(Number(cur.location_id))) {
    guard.add(Number(cur.location_id))
    const pid = Number(cur.parent_location_id)
    ancestors.add(pid)
    cur = layoutData.value.find(l => Number(l.location_id) === pid)
  }
  let best = null
  for (const r of openRounds.value) {
    if ((r.scope_mode || 'warehouse') !== 'partial') {
      best = r.round_id
      continue
    }
    const roots = (r.scope_location_ids || []).map(Number)
    if (roots.some(root => ancestors.has(root))) {
      if (best == null || Number(r.round_id) > Number(best)) best = r.round_id
    }
  }
  return best
}

const historyDrawerVisible = ref(false)
const historyLoading = ref(false)
const historyRounds = ref([])
const historySessionsMap = reactive({})

async function openStockTakeHistory() {
  historyDrawerVisible.value = true
  historyLoading.value = true
  historyRounds.value = []
  Object.keys(historySessionsMap).forEach(k => delete historySessionsMap[k])
  try {
    historyRounds.value = await listRounds()
  } catch (e) {
    ElMessage.error(e?.message || '加载历史失败')
  } finally {
    historyLoading.value = false
  }
}

async function toggleHistorySessions(r) {
  const id = r.round_id
  if (historySessionsMap[id]) {
    delete historySessionsMap[id]
    return
  }
  try {
    historySessionsMap[id] = await listSessionsForRound(id)
  } catch {
    historySessionsMap[id] = []
  }
}

const desktopStockTakeMode = ref(false)
const stampSaving = ref(false)
const desktopLineStates = reactive({})

function desktopLineKey(row) {
  return String(row.inventory_id || '')
}

function desktopLineState(row) {
  const k = desktopLineKey(row)
  if (!desktopLineStates[k]) {
    const openId =
      coveringRoundIdForLocation(row.storage_location_id || row.storage_location?.location_id) ||
      binDialogLocation.value?.open_stock_take_round_id ||
      openRound.value?.round_id
    const rid = row.mobile_stock_take_round_id
    const checked =
      !!row.mobile_stock_take_checked &&
      openId != null &&
      rid != null &&
      Number(rid) === Number(openId)
    const cq = checked ? row.mobile_stock_take_counted_qty : null
    desktopLineStates[k] = {
      checked,
      countedStr: cq != null && cq !== '' ? String(cq) : ''
    }
  }
  return desktopLineStates[k]
}

function syncDesktopLineStatesFromRows() {
  Object.keys(desktopLineStates).forEach(k => delete desktopLineStates[k])
  for (const row of binInventoryRows.value) {
    desktopLineState(row)
  }
}

function desktopInvRowCode(row) {
  return (
    (row?.warehouse_manual_code || '').trim() ||
    (row?.material_batch?.manual_batch_number || '').trim() ||
    (row?.material_batch?.system_batch_number || '').trim() ||
    (row?.inventory_id != null ? `#${row.inventory_id}` : '—')
  )
}

const uncheckedBinInvRows = computed(() =>
  binInventoryRows.value.filter(row => {
    if (!row?.inventory_id) return false
    return !desktopLineState(row).checked
  })
)

const stampedBinInvCount = computed(
  () => binInventoryRows.value.length - uncheckedBinInvRows.value.length
)

const canStampCurrentBin = computed(() => uncheckedBinInvRows.value.length === 0)

async function stampCurrentBin() {
  if (!binDialogLocation.value) return
  if (uncheckedBinInvRows.value.length > 0) {
    const labels = uncheckedBinInvRows.value.slice(0, 8).map(desktopInvRowCode)
    const more =
      uncheckedBinInvRows.value.length > 8
        ? ` 等共 ${uncheckedBinInvRows.value.length} 条`
        : ''
    ElMessage.warning(
      `尚有未勾「已盘」：${labels.join('、')}${more}。请全部勾选后再盖章；实物不在本库位的请先移库或报损。`
    )
    return
  }
  stampSaving.value = true
  try {
    const data = await stampLocation({
      storageLocationId: binDialogLocation.value.location_id,
      warehouseLocationId: selectedWarehouseId.value,
      locationName: binDialogLocation.value.location_name
    })
    if (data) {
      const patch = {
        operator_name: data.operator_name,
        checked_at: data.updated_at || data.created_at,
        session_id: data.session_id,
        round_id: data.round_id
      }
      binDialogLocation.value = { ...binDialogLocation.value, last_stock_take: patch }
      layoutData.value = layoutData.value.map(row =>
        Number(row.location_id) === Number(binDialogLocation.value.location_id)
          ? { ...row, last_stock_take: patch }
          : row
      )
      await refreshRoundProgress()
    }
  } finally {
    stampSaving.value = false
  }
}

async function voidBinStamp() {
  const sid = binDialogLocation.value?.last_stock_take?.session_id
  if (!sid) return
  const ok = await voidLatestStamp(sid)
  if (ok) {
    binDialogLocation.value = { ...binDialogLocation.value, last_stock_take: null }
    layoutData.value = layoutData.value.map(row =>
      Number(row.location_id) === Number(binDialogLocation.value.location_id)
        ? { ...row, last_stock_take: null }
        : row
    )
  }
}

async function onDesktopCheckChange(row, checked) {
  if (!row?.inventory_id || !binInOpenRound.value) return
  const st = desktopLineState(row)
  try {
    const res = await patchMobileStockTakeLineMark({
      inventory_id: row.inventory_id,
      warehouse_location_id: selectedWarehouseId.value,
      is_checked: !!checked,
      counted_quantity: checked
        ? (st.countedStr !== '' ? Number(st.countedStr) : undefined)
        : null
    })
    const data = res?.data || {}
    st.checked = !!data.mobile_stock_take_checked
    row.mobile_stock_take_checked = st.checked
    if (!st.checked) st.countedStr = ''
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '保存失败')
  }
}

async function persistDesktopCounted(row) {
  if (!row?.inventory_id || !binInOpenRound.value) return
  const st = desktopLineState(row)
  const raw = String(st.countedStr || '').trim()
  try {
    const res = await patchMobileStockTakeLineMark({
      inventory_id: row.inventory_id,
      warehouse_location_id: selectedWarehouseId.value,
      is_checked: true,
      counted_quantity: raw === '' ? null : Number(raw)
    })
    const data = res?.data || {}
    st.checked = !!data.mobile_stock_take_checked
    row.mobile_stock_take_checked = st.checked
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '保存失败')
  }
}

function locationLabelById(id) {
  const loc = layoutData.value.find(l => Number(l.location_id) === Number(id))
  return loc ? `${loc.location_name} (${loc.location_code || id})` : String(id)
}

const desktopQtyVisible = ref(false)
const desktopQtyMode = ref('overflow')
const desktopQtyRow = ref(null)
const desktopQtySubmitting = ref(false)
const desktopQtyForm = reactive({ deltaInput: '', actualInput: '', notes: '' })
const overflowTypeId = ref(null)
const lossTypeId = ref(null)
const yardUnitId = ref(null)

function pickTransactionTypeId(types, code, nameHints) {
  if (!Array.isArray(types) || !types.length) return null
  let t = types.find(x => x.type_code === code)
  if (t?.type_id) return t.type_id
  for (const h of nameHints) {
    t = types.find(x => (x.type_name || '').includes(h))
    if (t?.type_id) return t.type_id
  }
  return null
}

async function loadDesktopTxnMeta() {
  const extractList = (res) => {
    if (Array.isArray(res)) return res
    if (Array.isArray(res?.data)) return res.data
    if (Array.isArray(res?.items)) return res.items
    if (Array.isArray(res?.data?.items)) return res.data.items
    return []
  }
  try {
    const [typesRes, unitsRes] = await Promise.all([
      getInventoryTransactionTypes({ page: 1, per_page: 200, is_active: true }),
      getUnits({ page: 1, per_page: 500, is_active: true })
    ])
    const types = extractList(typesRes)
    overflowTypeId.value = pickTransactionTypeId(types, 'OVERFLOW_IN', ['报溢', '盘盈'])
    lossTypeId.value = pickTransactionTypeId(types, 'LOSS_OUT', ['报损', '盘亏'])
    const units = extractList(unitsRes)
    const yard =
      units.find(u => {
        const c = String(u.unit_code || '').toLowerCase()
        return c === 'yd' || c === 'yard'
      }) || units.find(u => u.unit_name === '码' || String(u.unit_name || '').includes('码'))
    yardUnitId.value = yard?.unit_id ?? null
    return { ok: !!(overflowTypeId.value && lossTypeId.value && yardUnitId.value) }
  } catch (_) {
    return { ok: false }
  }
}

async function openDesktopQty(row, mode) {
  if (!canAdjustStock.value) {
    ElMessage.warning('无库存作业权限')
    return
  }
  await loadDesktopTxnMeta()
  const needTypeId = mode === 'overflow' ? overflowTypeId.value : lossTypeId.value
  if (!needTypeId || !yardUnitId.value) {
    const miss = []
    if (mode === 'overflow' && !overflowTypeId.value) miss.push('报溢类型')
    if (mode === 'loss' && !lossTypeId.value) miss.push('报损类型')
    if (!yardUnitId.value) miss.push('单位「码」')
    ElMessage.warning(`无法报溢/报损：缺少 ${miss.join('、')}`)
    return
  }
  desktopQtyMode.value = mode
  desktopQtyRow.value = row
  desktopQtyForm.deltaInput = ''
  desktopQtyForm.actualInput = ''
  desktopQtyForm.notes = ''
  const cur = Number(row.physical_quantity || 0)
  const counted =
    row?.mobile_stock_take_counted_qty != null && row.mobile_stock_take_counted_qty !== ''
      ? Number(row.mobile_stock_take_counted_qty)
      : null
  if (counted != null && Number.isFinite(counted)) {
    desktopQtyForm.actualInput = String(counted)
    const diff = Math.round((counted - cur) * 10000) / 10000
    if (mode === 'overflow' && diff > 0) desktopQtyForm.deltaInput = String(diff)
    if (mode === 'loss' && diff < 0) desktopQtyForm.deltaInput = String(Math.abs(diff))
  }
  desktopQtyVisible.value = true
}

function parseQtyInput(s) {
  const n = parseFloat(String(s || '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : null
}

async function submitDesktopQty() {
  const row = desktopQtyRow.value
  if (!row) return
  const cur = Number(row.physical_quantity || 0)
  let qty
  const d = parseQtyInput(desktopQtyForm.deltaInput)
  const a = parseQtyInput(desktopQtyForm.actualInput)
  if (desktopQtyMode.value === 'overflow') {
    if (d != null && d > 0) qty = d
    else if (a != null) {
      qty = Math.round((a - cur) * 10000) / 10000
      if (qty <= 0) {
        ElMessage.warning('实际数量须大于账面')
        return
      }
    } else {
      ElMessage.warning('请填写增加数量或实际数量')
      return
    }
  } else if (d != null && d > 0) {
    if (d > cur) {
      ElMessage.warning('减少数量不能大于账面')
      return
    }
    qty = d
  } else if (a != null) {
    if (a >= cur) {
      ElMessage.warning('报损后须小于账面')
      return
    }
    qty = Math.round((cur - a) * 10000) / 10000
  } else {
    ElMessage.warning('请填写减少数量或实际数量')
    return
  }
  const typeId = desktopQtyMode.value === 'overflow' ? overflowTypeId.value : lossTypeId.value
  const batchId = row.material_batch_id || row.material_batch?.batch_id
  if (!batchId) {
    ElMessage.warning('该行缺少料号')
    return
  }
  desktopQtySubmitting.value = true
  try {
    const operator = userStore.user?.username || userStore.user?.name || '用户'
    const timeStr = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const noteParts = [`[盘点桌面 ${operator} ${timeStr}]`, desktopQtyForm.notes].filter(Boolean)
    const locId = row.storage_location_id || row.storage_location?.location_id
    const identityId = Number(
      row.material_identity?.identity_id ||
        row.material_identity?.material_identity_id ||
        row.material_batch?.material_identity_id
    )
    const res = await createInventoryTransaction({
      transaction_type_id: typeId,
      material_batch_id: batchId,
      storage_location_id: locId,
      location_id: locId,
      quantity: qty,
      unit_id: yardUnitId.value,
      notes: noteParts.join(' '),
      inventory_id: row.inventory_id,
      material_identity_id: Number.isFinite(identityId) && identityId > 0 ? identityId : undefined,
      is_adjustment: true,
      related_order_type: 'adjustment'
    })
    const result = interpretInventoryTransactionResponse(res, '已提交审批')
    if (notifyInventoryTransactionResult(result)) {
      desktopQtyVisible.value = false
      if (!result.pendingApproval && binDialogLocation.value) {
        await openBinInventoryDetail(binDialogLocation.value)
      }
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '失败')
  } finally {
    desktopQtySubmitting.value = false
  }
}

const desktopIntakeVisible = ref(false)
const desktopIntakeSubmitting = ref(false)
const desktopIntakeForm = reactive({
  storage_location_id: null,
  warehouse_manual_code: '',
  quantityStr: '',
  rollCountStr: '',
  applicant_notes: ''
})

function openDesktopIntake() {
  if (!canStockTakeEdit.value) {
    ElMessage.warning('无权限')
    return
  }
  desktopIntakeForm.storage_location_id =
    inventorySubtreeLocationIds.value.length === 1
      ? inventorySubtreeLocationIds.value[0]
      : binDialogLocation.value?.location_id || null
  desktopIntakeForm.warehouse_manual_code = ''
  desktopIntakeForm.quantityStr = ''
  desktopIntakeForm.rollCountStr = ''
  desktopIntakeForm.applicant_notes = ''
  desktopIntakeVisible.value = true
}

async function submitDesktopIntake() {
  const code = (desktopIntakeForm.warehouse_manual_code || '').trim()
  const q = parseQtyInput(desktopIntakeForm.quantityStr)
  if (!desktopIntakeForm.storage_location_id || !code || q == null || q <= 0) {
    ElMessage.warning('请填写库位、仓库料号与有效数量')
    return
  }
  let rollCount
  if ((desktopIntakeForm.rollCountStr || '').trim() !== '') {
    const rc = parseInt(String(desktopIntakeForm.rollCountStr).trim(), 10)
    if (!Number.isFinite(rc) || rc < 0) {
      ElMessage.warning('支数须为非负整数')
      return
    }
    rollCount = rc
  }
  desktopIntakeSubmitting.value = true
  try {
    const payload = {
      warehouse_location_id: selectedWarehouseId.value,
      storage_location_id: Number(desktopIntakeForm.storage_location_id),
      warehouse_manual_code: code,
      quantity: q,
      applicant_notes: (desktopIntakeForm.applicant_notes || '').trim() || undefined
    }
    if (rollCount != null) payload.roll_count = rollCount
    const res = await createInventoryManualCodeIntakeRequest(payload)
    if (res?.success !== false) {
      ElMessage.success(res?.message || '已提交申请')
      desktopIntakeVisible.value = false
    } else {
      ElMessage.error(res?.message || '提交失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '提交失败')
  } finally {
    desktopIntakeSubmitting.value = false
  }
}

const warehouseList = ref([])
const layoutData = ref([])
const editMode = ref(false)
const gridSize = ref(80)
const showGrid = ref(true)
const gridColumns = ref(20)
const gridRows = ref(15)
const containerElement = ref(null)
const selectedLocationId = ref(null)
const keyPressed = ref(null)
const moveInterval = ref(null)
const currentMoveDirection = ref(null)
const editFormRef = ref(null)

/** 浏览模式：面包屑路径（含当前仓库根） */
const browsePath = ref([])

const binInventoryDialogVisible = ref(false)
const binInventoryLoading = ref(false)
const binInventoryRows = ref([])
const binDialogLocation = ref(null)
/** 当前汇总所包含的库位 ID（含本级及下级） */
const inventorySubtreeLocationIds = ref([])

// 预定义颜色
const predefineColors = ref([
  '#409EFF',
  '#67C23A',
  '#E6A23C',
  '#F56C6C',
  '#909399',
  '#ff4500',
  '#ff8c00',
  '#ffd700',
  '#90ee90',
  '#00ced1',
  '#1e90ff',
  '#c71585'
])

// 编辑对话框
const showLocationEditDialog = ref(false)
const editingLocation = reactive({
  location_id: null,
  location_name: '',
  layout_x: 0,
  layout_y: 0,
  layout_width: 1,
  layout_height: 1,
  layout_color: '#409EFF',
  location_type: ''
})

// 计算属性
const selectedWarehouse = computed(() => {
  return warehouseList.value.find(w => w.location_id === selectedWarehouseId.value)
})

const selectedLocation = computed(() => {
  return layoutData.value.find(l => l.location_id === selectedLocationId.value)
})

const browseParentId = computed(() => {
  const path = browsePath.value
  if (!path.length) return selectedWarehouseId.value
  return path[path.length - 1].id
})

function sortLocationsNatural(list) {
  return [...list].sort((a, b) => {
    const ca = String(a.location_code || a.location_name || '').trim()
    const cb = String(b.location_code || b.location_name || '').trim()
    return ca.localeCompare(cb, 'zh-CN', { numeric: true, sensitivity: 'base' })
  })
}

const folderChildren = computed(() => {
  const pid = browseParentId.value
  if (!pid) return []
  const raw = layoutData.value.filter(l => Number(l.parent_location_id) === Number(pid))
  const seen = new Map()
  for (const l of raw) {
    const id = Number(l.location_id)
    if (!seen.has(id)) seen.set(id, l)
  }
  return sortLocationsNatural(Array.from(seen.values()))
})

const folderChildrenWithMeta = computed(() =>
  folderChildren.value.map(loc => ({
    ...loc,
    hasChildren: locationHasChildren(loc)
  }))
)

const STORAGE_SLOT_TYPES = new Set(['bin', 'pallet'])

function isStorageSlotType (t) {
  return STORAGE_SLOT_TYPES.has(t)
}

function locationNodeInventoryQty (loc) {
  const s = loc?.inventory_summary
  if (!s) return 0
  return Number(s.total_quantity ?? 0)
}

/** 浏览列表：末级卡板/库位、已启用、实物数量为 0 */
function showEmptyCharForBrowseItem (item) {
  if (item?.is_active === false) return false
  if (item?.hasChildren) return false
  if (!isStorageSlotType(item?.location_type)) return false
  return locationNodeInventoryQty(item) <= 0
}

/** 平面网格：卡板/库位格内无在库实物 */
function showEmptyCharForLayoutBlock (location) {
  if (location?.is_active === false) return false
  if (!isStorageSlotType(location?.location_type)) return false
  return locationNodeInventoryQty(location) <= 0
}

const editModeTip = computed(() => {
  let tip = '编辑模式：左键选择库位，右键取消选择，'
  if (selectedLocation.value) {
    tip += `已选中 ${selectedLocation.value.location_name}，`
  }
  tip += '使用方向键连续移动，或使用下方按钮调整尺寸'
  return tip
})

// 容器尺寸
const containerWidth = computed(() => gridColumns.value * gridSize.value)
const containerHeight = computed(() => gridRows.value * gridSize.value)

// 容器样式
const containerStyle = computed(() => {
  return {
    width: `${containerWidth.value}px`,
    height: `${containerHeight.value}px`,
    backgroundSize: `${gridSize.value}px ${gridSize.value}px`,
    backgroundImage: showGrid.value ? 
      'linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)' : 'none',
    // 防止屏幕晃动
    transform: 'translateZ(0)',
    willChange: 'auto'
  }
})

// 网格单元格
const gridCells = computed(() => {
  const cells = []
  for (let y = 0; y < gridRows.value; y++) {
    for (let x = 0; x < gridColumns.value; x++) {
      cells.push({ x, y })
    }
  }
  return cells
})

// 可见的库位
const visibleLocations = computed(() => {
  const locations = layoutData.value.filter(location => 
    location.layout_x !== null && 
    location.layout_y !== null
  )
  return locations
})

// 生命周期
onMounted(() => {
  console.log('组件挂载，开始初始化')
  loadDesktopTxnMeta()
  loadWarehouseList().then(() => {
    // 确保仓库列表加载完成后再加载网格设置
    if (selectedWarehouseId.value) {
      console.log('仓库列表加载完成，开始加载网格设置')
      loadGridSettings()
    }
  })
  nextTick(() => {
    if (containerElement.value) {
      containerElement.value.focus()
    }
  })
})

onUnmounted(() => {
  // 清理事件监听器
  stopContinuousMove()
})

// 保存网格设置到本地存储
const saveGridSettings = () => {
  if (!selectedWarehouseId.value) {
    console.log('无法保存网格设置：未选择仓库')
    return
  }
  
  const settings = {
    gridSize: gridSize.value,
    gridColumns: gridColumns.value,
    gridRows: gridRows.value,
    showGrid: showGrid.value,
    warehouseId: selectedWarehouseId.value,
    timestamp: new Date().getTime()
  }
  
  const storageKey = `warehouseGridSettings_${selectedWarehouseId.value}`
  localStorage.setItem(storageKey, JSON.stringify(settings))
  
  console.log(`保存网格设置: ${storageKey}`, settings)
  ElMessage.success('网格设置已保存')
}

// 加载网格设置从本地存储
const loadGridSettings = () => {
  if (!selectedWarehouseId.value) {
    console.log('无法加载网格设置：未选择仓库')
    return
  }
  
  try {
    const storageKey = `warehouseGridSettings_${selectedWarehouseId.value}`
    const saved = localStorage.getItem(storageKey)
    
    console.log(`尝试加载网格设置: ${storageKey}`, saved)
    
    if (saved) {
      const settings = JSON.parse(saved)
      
      // 应用保存的设置
      gridSize.value = settings.gridSize || 80
      gridColumns.value = settings.gridColumns || 20
      gridRows.value = settings.gridRows || 15
      showGrid.value = settings.showGrid !== undefined ? settings.showGrid : true
      
      console.log('加载网格设置成功:', {
        gridSize: gridSize.value,
        gridColumns: gridColumns.value,
        gridRows: gridRows.value,
        showGrid: showGrid.value
      })
    } else {
      console.log('没有找到保存的网格设置，使用默认值')
      // 使用默认值，但不保存到本地存储
      gridSize.value = 80
      gridColumns.value = 20
      gridRows.value = 15
      showGrid.value = true
    }
  } catch (error) {
    console.error('加载网格设置失败:', error)
    // 出错时使用默认值
    gridSize.value = 80
    gridColumns.value = 20
    gridRows.value = 15
    showGrid.value = true
  }
}

// 检查数据 - 增强版
const checkLocationData = () => {
  console.log('=== 网格设置状态详细检查 ===')
  console.log('当前仓库ID:', selectedWarehouseId.value)
  console.log('总库位数量:', layoutData.value.length)
  
  // 检查所有仓库的网格设置
  const allStorageKeys = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('warehouseGridSettings_')) {
      allStorageKeys.push(key)
    }
  }
  
  console.log('所有保存的网格设置:', allStorageKeys)
  
  // 检查当前仓库的设置
  const currentStorageKey = `warehouseGridSettings_${selectedWarehouseId.value}`
  const savedSettings = localStorage.getItem(currentStorageKey)
  console.log('当前仓库的网格设置:', savedSettings ? JSON.parse(savedSettings) : '未保存')
  
  console.log('当前内存中的网格设置:', {
    gridSize: gridSize.value,
    gridColumns: gridColumns.value, 
    gridRows: gridRows.value,
    showGrid: showGrid.value
  })
  
  const typeCount = {}
  const layoutIssues = []
  
  layoutData.value.forEach(location => {
    const type = location.location_type
    typeCount[type] = typeCount[type] ? typeCount[type] + 1 : 1
    
    // 检查布局数据完整性
    if (location.layout_x === null || location.layout_y === null) {
      layoutIssues.push(`${location.location_name} 缺少布局坐标`)
    }
  })
  
  console.log('库位类型分布:', typeCount)
  console.log('布局问题:', layoutIssues)
  
  ElMessage.info(`检查完成：${layoutData.value.length}个库位，${layoutIssues.length}个布局问题`)
}

// 更新容器尺寸
const updateContainerSize = () => {
  nextTick(() => {
    // 触发重新计算
  })
}

// 处理仓库切换
const handleWarehouseChange = async () => {
  console.log('开始切换仓库，当前仓库ID:', selectedWarehouseId.value)
  
  // 先保存当前仓库的网格设置
  if (selectedWarehouseId.value) {
    console.log('保存当前仓库的网格设置')
    saveGridSettings()
  }
  
  // 等待保存完成
  await new Promise(resolve => setTimeout(resolve, 100))
  
  // 加载新仓库的布局
  console.log('加载新仓库布局')
  await loadWarehouseLayout()
  
  // 重新加载新仓库的网格设置
  console.log('加载新仓库的网格设置')
  loadGridSettings()
}

// 加载仓库列表
const loadWarehouseList = async () => {
  try {
    const response = await getStorageLocations({ 
      location_type: 'warehouse',
      is_active: true
    })
    warehouseList.value = response.data
    if (warehouseList.value.length > 0) {
      selectedWarehouseId.value = warehouseList.value[0].location_id
      await loadWarehouseLayout()
    }
  } catch (error) {
    ElMessage.error('加载仓库列表失败')
    console.error('加载仓库列表错误:', error)
  }
}

// 加载仓库布局
const loadWarehouseLayout = async () => {
  if (!selectedWarehouseId.value) return
  
  loading.value = true
  try {
    const response = await getWarehouseLayout({ 
      warehouse_id: selectedWarehouseId.value
    })
    layoutData.value = response.data || []
    if (Array.isArray(response.open_stock_take_rounds)) {
      applyOpenRoundsList(response.open_stock_take_rounds)
    } else if (response.open_stock_take_round) {
      applyOpenRoundsList([response.open_stock_take_round])
    } else {
      await loadOpenRound()
    }
    for (const r of openRounds.value) {
      await refreshRoundProgress(r.round_id, { mergeStamps: true })
    }
    
    console.log('加载的布局数据:', layoutData.value)
    
    // 注意：这里不要重置选中状态，保持用户的选择
    // selectedLocationId.value = null
    resetBrowsePath()
    
  } catch (error) {
    ElMessage.error('加载仓库布局失败')
    console.error('加载仓库布局错误:', error)
  } finally {
    loading.value = false
  }
}

const resetBrowsePath = () => {
  const w = selectedWarehouse.value
  if (w) {
    browsePath.value = [{ id: w.location_id, name: w.location_name }]
  } else {
    browsePath.value = []
  }
}

const locationHasChildren = (loc) => {
  if (!loc?.location_id) return false
  return layoutData.value.some(
    l => Number(l.parent_location_id) === Number(loc.location_id)
  )
}

const formatQty = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '0'
  const x = Number(n)
  return Number.isInteger(x) ? String(x) : x.toFixed(2)
}

/** 库位支数（后端 warehouse-layout.inventory_summary.total_branch_rolls） */
const formatBranchRolls = (summary) => {
  if (!summary || summary.total_branch_rolls == null) return '0'
  const x = Number(summary.total_branch_rolls)
  return Number.isFinite(x) ? String(Math.round(x)) : '0'
}

const binInventoryRowClassName = ({ row }) =>
  isDispatchCompleteTransitRow(row) ? 'dispatch-complete-transit-row' : ''

const formatStockTakeTime = (iso) => {
  if (!iso) return '—'
  return String(iso).replace('T', ' ').slice(0, 19)
}

const goBrowseCrumb = (index) => {
  browsePath.value = browsePath.value.slice(0, index + 1)
}

const enterBrowseFolder = (loc) => {
  browsePath.value.push({ id: loc.location_id, name: loc.location_name })
}

const onBrowseRowClick = (item) => {
  if (item.hasChildren) {
    enterBrowseFolder(item)
  } else {
    openBinInventoryDetail(item)
  }
}

const collectSubtreeLocationIds = (rootId) => {
  const ids = []
  const dfs = (id) => {
    ids.push(id)
    layoutData.value
      .filter(l => Number(l.parent_location_id) === Number(id))
      .forEach(ch => dfs(ch.location_id))
  }
  dfs(rootId)
  return ids
}

const openBinInventoryDetail = async (loc) => {
  binDialogLocation.value = loc
  const ids = collectSubtreeLocationIds(loc.location_id)
  inventorySubtreeLocationIds.value = ids
  binInventoryDialogVisible.value = true
  binInventoryLoading.value = true
  binInventoryRows.value = []
  try {
    if (ids.length === 0) {
      return
    }
    if (ids.length > 80) {
      ElMessage.warning('下级位置较多，正在并行汇总，请稍候')
    }
    const responses = await Promise.all(
      ids.map(id =>
        getMaterialInventory({
          storage_location_id: id,
          per_page: 500,
          page: 1,
          only_inventory_row: true
        })
      )
    )
    const merged = []
    const seen = new Set()
    for (const res of responses) {
      if (!res.success || !res.data) continue
      for (const row of res.data) {
        const iid = row.inventory_id
        if (iid != null) {
          if (seen.has(iid)) continue
          seen.add(iid)
        }
        merged.push(row)
      }
    }
    binInventoryRows.value = merged
    syncDesktopLineStatesFromRows()
  } catch (error) {
    console.error(error)
    ElMessage.error('加载库存失败')
  } finally {
    binInventoryLoading.value = false
  }
}

const onBinDialogClosed = () => {
  binDialogLocation.value = null
  binInventoryRows.value = []
  inventorySubtreeLocationIds.value = []
  desktopStockTakeMode.value = false
  Object.keys(desktopLineStates).forEach(k => delete desktopLineStates[k])
}

const goInventoryPageForBin = () => {
  if (!binDialogLocation.value || !selectedWarehouseId.value) return
  router.push({
    path: '/inventory/material-inventory',
    query: {
      location_id: binDialogLocation.value.location_id,
      location_name: binDialogLocation.value.location_name,
      warehouse_id: selectedWarehouseId.value,
      from_layout: 'true'
    }
  })
}

// 获取单元格样式
const getCellStyle = (cell) => {
  const style = {
    left: `${cell.x * gridSize.value}px`,
    top: `${cell.y * gridSize.value}px`,
    width: `${gridSize.value}px`,
    height: `${gridSize.value}px`
  }
  return style
}

// 获取库位样式
const getLocationStyle = (location) => {
  const x = parseInt(location.layout_x) || 0
  const y = parseInt(location.layout_y) || 0
  const width = parseInt(location.layout_width) || 1
  const height = parseInt(location.layout_height) || 1
  
  const left = x * gridSize.value
  const top = y * gridSize.value
  const blockWidth = width * gridSize.value - 4
  const blockHeight = height * gridSize.value - 4
  
  const style = {
    left: `${left}px`,
    top: `${top}px`,
    width: `${blockWidth}px`,
    height: `${blockHeight}px`,
    backgroundColor: location.layout_color || getDefaultColor(location.location_type),
    // 防止屏幕晃动
    transform: 'translateZ(0)',
    willChange: 'auto'
  }
  
  return style
}

// 切换编辑模式
const toggleEditMode = () => {
  if (!canEditLocations.value) {
    ElMessage.warning('您没有库位管理编辑权限')
    return
  }
  editMode.value = !editMode.value
  // 退出编辑模式时取消选择，并回到仓库根浏览
  if (!editMode.value) {
    selectedLocationId.value = null
    resetBrowsePath()
  }
  
  nextTick(() => {
    if (containerElement.value) {
      containerElement.value.focus()
    }
  })
}

// 重置视图 - 只重置临时状态，不重置保存的设置
const resetView = () => {
  console.log('重置视图，但保留网格设置')
  selectedLocationId.value = null
  // 不要重置网格设置，让用户手动保存或使用已保存的设置
  loadWarehouseLayout()
  // 重新加载当前仓库的网格设置
  loadGridSettings()
}

// 自动排列
const autoArrange = async () => {
  let x = 0, y = 0
  let maxRowHeight = 1
  
  // 按类型分组排列
  const locationsByType = {}
  layoutData.value.forEach(location => {
    const type = location.location_type
    if (!locationsByType[type]) {
      locationsByType[type] = []
    }
    locationsByType[type].push(location)
  })
  
  // 定义类型排列顺序
  const typeOrder = ['warehouse', 'area', 'shelf', 'pallet', 'bin']
  
  for (const type of typeOrder) {
    if (locationsByType[type]) {
      for (const location of locationsByType[type]) {
        // 如果当前行放不下，换到下一行
        const locationWidth = location.layout_width || 1
        if (x + locationWidth > gridColumns.value) {
          x = 0
          y += maxRowHeight
          maxRowHeight = 1
        }
        
        location.layout_x = x
        location.layout_y = y
        location.layout_width = locationWidth
        location.layout_height = location.layout_height || 1
        
        // 更新最大行高
        maxRowHeight = Math.max(maxRowHeight, location.layout_height)
        
        x += locationWidth
        
        // 立即保存布局
        try {
          await saveLocationLayout(location)
        } catch (error) {
          console.error(`保存库位 ${location.location_name} 布局失败:`, error)
        }
      }
      
      // 类型之间空一行
      if (locationsByType[type].length > 0) {
        x = 0
        y += maxRowHeight + 1
        maxRowHeight = 1
      }
    }
  }
  
  ElMessage.success('自动排列完成')
  await loadWarehouseLayout()
}

// 键盘事件处理 - 支持连续移动
const handleKeydown = (event) => {
  if (!editMode.value || !selectedLocationId.value) return
  
  const selectedLocation = layoutData.value.find(l => l.location_id === selectedLocationId.value)
  if (!selectedLocation) return

  // 防止重复处理
  if (keyPressed.value === event.key) return
  keyPressed.value = event.key

  let newX = parseInt(selectedLocation.layout_x) || 0
  let newY = parseInt(selectedLocation.layout_y) || 0
  let moved = false

  switch (event.key) {
    case 'ArrowLeft':
      newX = Math.max(0, newX - 1)
      moved = true
      break
    case 'ArrowRight':
      newX = Math.min(gridColumns.value - (parseInt(selectedLocation.layout_width) || 1), newX + 1)
      moved = true
      break
    case 'ArrowUp':
      newY = Math.max(0, newY - 1)
      moved = true
      break
    case 'ArrowDown':
      newY = Math.min(gridRows.value - (parseInt(selectedLocation.layout_height) || 1), newY + 1)
      moved = true
      break
    default:
      return
  }
  
  if (moved) {
    event.preventDefault()
    
    console.log(`移动库位 ${selectedLocation.location_name}: (${selectedLocation.layout_x}, ${selectedLocation.layout_y}) -> (${newX}, ${newY})`)
    
    // 更新位置
    selectedLocation.layout_x = newX
    selectedLocation.layout_y = newY
    
    // 自动保存
    saveLocationLayout(selectedLocation)
  }
}

// 修复ESLint错误：移除未使用的event参数
const handleKeyup = () => {
  keyPressed.value = null
}

// 右键点击处理
const handleRightClick = () => {
  // 在空白处右键取消选择
  if (editMode.value) {
    selectedLocationId.value = null
  }
}

// 库位右键点击处理
const handleLocationRightClick = () => {
  if (editMode.value) {
    // 编辑模式下右键取消选择
    selectedLocationId.value = null
  }
}

// 连续移动功能 - 改进版
const startContinuousMove = (direction) => {
  if (!selectedLocation.value) return
  
  currentMoveDirection.value = direction
  stopContinuousMove() // 先停止之前的移动
  
  // 立即移动一次
  moveLocationByDirection(direction)
  
  // 设置连续移动 - 更快的响应
  moveInterval.value = setInterval(() => {
    moveLocationByDirection(direction)
  }, 50) // 每50毫秒移动一次，更流畅
}

const stopContinuousMove = () => {
  if (moveInterval.value) {
    clearInterval(moveInterval.value)
    moveInterval.value = null
  }
  currentMoveDirection.value = null
}

const moveLocationByDirection = (direction) => {
  if (!selectedLocation.value) return
  
  let newX = parseInt(selectedLocation.value.layout_x) || 0
  let newY = parseInt(selectedLocation.value.layout_y) || 0

  switch (direction) {
    case 'left':
      newX = Math.max(0, newX - 1)
      break
    case 'right':
      newX = Math.min(gridColumns.value - (parseInt(selectedLocation.value.layout_width) || 1), newX + 1)
      break
    case 'up':
      newY = Math.max(0, newY - 1)
      break
    case 'down':
      newY = Math.min(gridRows.value - (parseInt(selectedLocation.value.layout_height) || 1), newY + 1)
      break
  }
  
  // 只有当位置发生变化时才保存
  if (newX !== selectedLocation.value.layout_x || newY !== selectedLocation.value.layout_y) {
    selectedLocation.value.layout_x = newX
    selectedLocation.value.layout_y = newY
    saveLocationLayout(selectedLocation.value)
  }
}

// 尺寸调整
const handleSizeChange = () => {
  if (selectedLocation.value) {
    // 确保尺寸在合理范围内
    selectedLocation.value.layout_width = Math.max(1, Math.min(50, selectedLocation.value.layout_width))
    selectedLocation.value.layout_height = Math.max(1, Math.min(50, selectedLocation.value.layout_height))
    saveLocationLayout(selectedLocation.value)
  }
}

// 库位点击处理
const handleLocationClick = (location) => {
  if (editMode.value) {
    // 编辑模式下选择库位
    selectedLocationId.value = location.location_id
    console.log(`选中库位: ${location.location_name} (${location.layout_x}, ${location.layout_y})`)
  } else {
    // 非编辑模式下跳转
    router.push({
      path: '/inventory/material-inventory',
      query: { 
        location_id: location.location_id,
        location_name: location.location_name,
        warehouse_id: selectedWarehouseId.value,
        from_layout: 'true'
      }
    })
  }
}

// 库位双击处理
const handleLocationDoubleClick = (location) => {
  if (editMode.value) {
    // 编辑模式下打开编辑对话框
    openEditDialog(location)
  }
}

// 打开编辑对话框 - 修复数据传递问题
const openEditDialog = (location) => {
  console.log('打开编辑对话框:', location)
  Object.assign(editingLocation, {
    location_id: parseInt(location.location_id),
    location_name: location.location_name,
    layout_x: parseInt(location.layout_x) || 0,
    layout_y: parseInt(location.layout_y) || 0,
    layout_width: parseInt(location.layout_width) || 1,
    layout_height: parseInt(location.layout_height) || 1,
    layout_color: location.layout_color || getDefaultColor(location.location_type),
    location_type: location.location_type // 确保包含类型信息
  })
  showLocationEditDialog.value = true
}

// 重置编辑表单
const resetEditingLocation = () => {
  if (editFormRef.value) {
    editFormRef.value.resetFields()
  }
  Object.assign(editingLocation, {
    location_id: null,
    location_name: '',
    layout_x: 0,
    layout_y: 0,
    layout_width: 1,
    layout_height: 1,
    layout_color: '#409EFF',
    location_type: ''
  })
}

// 对话框保存处理
const handleDialogSave = async () => {
  await saveLocationLayout(editingLocation)
}

// 保存库位布局 - 修复保存问题
const saveLocationLayout = async (location) => {
  console.log('保存库位数据:', location)
  
  // 验证必要字段 - 修复验证逻辑
  if (!location || !location.location_id) {
    console.error('库位ID为空:', location)
    ElMessage.error('库位ID不能为空，请检查数据')
    return false
  }
  
  // 准备数据 - 确保包含所有必要字段
  const saveData = {
    location_id: parseInt(location.location_id),
    layout_x: parseInt(location.layout_x) || 0,
    layout_y: parseInt(location.layout_y) || 0,
    layout_width: parseInt(location.layout_width) || 1,
    layout_height: parseInt(location.layout_height) || 1,
    layout_color: location.layout_color || getDefaultColor(location.location_type)
  }

  console.log('发送保存数据:', saveData)

  try {
    const response = await updateLocationLayout(saveData)
    console.log('保存响应:', response)
    
    if (response.success) {
      // 如果是通过对话框保存，关闭对话框
      if (location === editingLocation) {
        ElMessage.success('布局保存成功')
        showLocationEditDialog.value = false
      }
      // 不再重新加载整个布局，避免取消选中状态
      // 只更新当前库位的数据
      const index = layoutData.value.findIndex(l => l.location_id === location.location_id)
      if (index !== -1) {
        Object.assign(layoutData.value[index], response.data)
      }
      return true
    } else {
      ElMessage.error(response.message || '保存布局失败')
      return false
    }
  } catch (error) {
    console.error('保存布局错误详情:', error)
    const errorMsg = error.response?.data?.message || error.message || '保存布局失败'
    ElMessage.error('保存布局失败: ' + errorMsg)
    return false
  }
}

// 工具函数
const getLocationTypeText = (type) => {
  const types = {
    warehouse: '仓库',
    area: '区域',
    shelf: '货架',
    pallet: '卡板',
    bin: '库位'
  }
  return types[type] || type
}

const getLocationTypeTagType = (type) => {
  const types = {
    warehouse: 'primary',
    area: 'success',
    shelf: 'warning',
    pallet: 'warning',
    bin: 'info'
  }
  return types[type] || 'info'
}

const getIconComponent = (type) => {
  const icons = {
    warehouse: OfficeBuilding,
    area: MapLocation,
    shelf: Grid,
    pallet: Collection,
    bin: Box
  }
  return icons[type] || Box
}

const getIconColor = (type) => {
  const colors = {
    warehouse: '#409EFF',
    area: '#67C23A',
    shelf: '#E6A23C',
    pallet: '#F56C6C',
    bin: '#909399'
  }
  return colors[type] || '#909399'
}

const getDefaultColor = (type) => {
  const colors = {
    warehouse: '#409EFF',
    area: '#67C23A',
    shelf: '#E6A23C',
    pallet: '#F56C6C',
    bin: '#909399'
  }
  return colors[type] || '#409EFF'
}
</script>

<style scoped>
.warehouse-layout-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.title-block h2 {
  margin: 0 0 4px 0;
}

.title-hint {
  margin: 0;
  font-size: 13px;
  color: #909399;
  font-weight: normal;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.layout-controls {
  margin-bottom: 20px;
}

.control-panel {
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  flex-wrap: wrap;
  gap: 10px;
}

.selected-location-controls {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.location-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 15px;
}

.size-controls, .move-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.move-tip {
  font-size: 12px;
  color: #666;
  margin-left: 5px;
}

.warehouse-layout-container {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 20px;
  background: white;
  min-height: 600px;
  /* 不设 max-height/overflow，避免与 Layout .main-viewport 叠双滚动条；整页由主视口统一滚动 */
  transform: translateZ(0);
  will-change: auto;
}

.warehouse-map-canvas {
  position: relative;
  margin: 0 auto;
  outline: none;
  border: 1px solid #e0e0e0;
  background: white;
  /* 防止屏幕晃动 */
  transform: translateZ(0);
  will-change: auto;
  /* 添加平滑滚动 */
  overflow: hidden;
}

.grid-cell {
  position: absolute;
  border: 1px dashed rgba(0,0,0,0.1);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  color: #ccc;
  /* 防止屏幕晃动 */
  transform: translateZ(0);
  will-change: auto;
}

.grid-coord {
  opacity: 0.5;
}

.location-block {
  position: absolute;
  border: 2px solid transparent;
  border-radius: 4px;
  padding: 8px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  z-index: 1;
  /* 防止屏幕晃动 */
  transform: translateZ(0);
  will-change: auto;
  /* 减少动画效果 */
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.location-block:hover {
  border-color: #409EFF;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0,0,0,0.15);
  z-index: 2;
}

.location-block.selected {
  border-color: #F56C6C;
  border-width: 3px;
  z-index: 3;
  box-shadow: 0 0 8px rgba(245, 108, 108, 0.4);
  transform: translateY(-1px);
}

.location-block.dispatch-complete-transit-block .location-content {
  color: #fef0f0;
}

.location-block.dispatch-complete-transit-block .location-name,
.location-block.dispatch-complete-transit-block .location-position,
.location-block.dispatch-complete-transit-block .inventory-stats,
.location-block.dispatch-complete-transit-block .location-stock-take {
  color: #ffcccc;
}

.location-block.dispatch-complete-transit-block {
  border-color: #f56c6c !important;
  box-shadow: 0 2px 8px rgba(245, 108, 108, 0.35);
}

:deep(.dispatch-complete-transit-row) {
  color: #f56c6c;
}

:deep(.dispatch-complete-transit-row td) {
  color: #f56c6c;
}

.location-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  font-size: 12px;
  overflow: hidden;
  padding: 4px;
}

.location-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 5px;
  gap: 2px;
}

.location-type-tag {
  font-size: 8px;
  padding: 0 3px;
  height: 14px;
  line-height: 14px;
  width: 100%;
  text-align: center;
}

.location-name {
  font-weight: bold;
  text-align: center;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  /* 增大字体大小 - 从11px增加到16px，增加约45% */
  font-size: 16px;
  /* 调整行高以确保文本垂直居中 */
  line-height: 1.2;
  /* 增加一点上边距，使文本在方块中更居中 */
  margin-top: 2px;
}

.location-position {
  font-size: 9px;
  text-align: center;
  opacity: 0.8;
  margin-bottom: 5px;
}

.inventory-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.inventory-stats {
  display: flex;
  justify-content: space-around;
  font-size: 9px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1px;
}

/* 与库位管理类似的文件夹卡片列表 */
.wl-folder-view {
  min-height: 400px;
}

.breadcrumb-nav {
  padding: 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 16px;
}

.breadcrumb-nav :deep(.el-breadcrumb__item) {
  display: inline-flex;
  align-items: center;
}

.empty-state {
  padding: 40px;
  text-align: center;
}

.wl-folder-view .folder-list {
  padding: 10px 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.wl-folder-view .folder-item-row {
  display: flex;
  flex-direction: column;
  padding: 16px;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.2s;
  box-sizing: border-box;
  min-height: 120px;
}

.wl-folder-view .folder-item-row.clickable {
  cursor: pointer;
}

.wl-folder-view .folder-item-row.clickable:hover {
  background-color: #f5f7fa;
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
  transform: translateY(-2px);
}

.wl-folder-view .folder-item-row.has-children {
  border-left: 3px solid #409eff;
}

.wl-folder-view .folder-item-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.wl-folder-view .folder-item-content .type-icon {
  font-size: 32px;
  width: 32px;
  height: 32px;
  margin-bottom: 4px;
}

.wl-folder-view .folder-item-content .folder-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.wl-folder-view .folder-item-content .folder-code {
  color: #909399;
  font-size: 13px;
}

.wl-folder-view .folder-item-content .folder-count {
  color: #409eff;
  font-size: 12px;
}

.folder-inv-hint {
  font-size: 12px;
  color: #606266;
}

.folder-inv-hint.muted {
  color: #c0c4cc;
}

.folder-stock-take {
  font-size: 12px;
  color: #c62828;
  font-weight: 600;
  margin-top: 4px;
  width: 100%;
}

.location-stock-take {
  font-size: 11px;
  color: #c62828;
  font-weight: 600;
  margin-top: 4px;
  line-height: 1.3;
}

.wl-storage-empty-char {
  font-size: 2.75rem;
  font-weight: 800;
  color: #67c23a;
  line-height: 1;
  margin: 4px 0 8px;
  letter-spacing: 0.15em;
  user-select: none;
}

.location-block-empty-char {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(1.5rem, 5vw, 2.5rem);
  font-weight: 800;
  color: #95d475;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  user-select: none;
}

.wl-folder-view .folder-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.bin-dialog-sub {
  font-size: 13px;
  color: #909399;
  margin-bottom: 12px;
}

.bin-dialog-take {
  font-size: 13px;
  color: #67c23a;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.bin-st-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.st-hist-round {
  padding: 12px 0;
  border-bottom: 1px solid #ebeef5;
}

.st-hist-round__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.st-hist-round__meta {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.st-hist-sessions {
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: 12px;
  color: #606266;
}

.st-hist-sessions .muted {
  color: #c0c4cc;
  list-style: none;
  margin-left: -18px;
}
</style>

<!-- 日期面板 teleport 到 body，需全局样式才能盖过弹窗 -->
<style>
.m-stock-take-datepicker-popper.el-popper,
.el-picker__popper.m-stock-take-datepicker-popper,
.m-stock-take-datepicker-popper {
  z-index: 5700 !important;
}
</style>